import { LoaderFunctionArgs, replace } from "react-router";
import { publicRouteLoader, authenticatedRouteLoader } from "./loaders";

// Helper functions to convert URLs to route paths
export const replaceFirstSlash = (path: string) => path.replace(/^\//, "");
export const replaceApp = (path: string) => path.replace("/app/", "");

type ImportFn<TProps extends object> = () => Promise<{
  default: React.ComponentType<TProps>;
}>;

// Lazy route definitions using route.lazy() API
export const createLazyRoute = <TProps extends object>(
  importFn: ImportFn<TProps>,
) => ({
  lazy: async () => {
    const module = await importFn();
    return {
      Component: module.default,
    };
  },
});

export const createLazyRouteWithProps = <TProps extends object>(
  importFn: ImportFn<TProps>,
  props: TProps,
) => ({
  lazy: async () => {
    const module = await importFn();
    return {
      Component: () => <module.default {...props} />,
    };
  },
});

export const createLazyRouteWithAuthLoader = <TProps extends object>(
  importFn: ImportFn<TProps>,
) => ({
  ...createLazyRoute(importFn),
  loader: authenticatedRouteLoader,
});

export const createLazyRouteWithAuthLoaderAndProps = <TProps extends object>(
  importFn: ImportFn<TProps>,
  props: TProps,
) => ({
  ...createLazyRouteWithProps(importFn, props),
  loader: authenticatedRouteLoader,
});

export const publicRoute = <TProps extends object>(
  path: string,
  importFn: ImportFn<TProps>,
) => ({
  path: replaceFirstSlash(path),
  loader: publicRouteLoader,
  ...createLazyRoute(importFn),
});

export const publicRouteWithProps = <TProps extends object>(
  path: string,
  importFn: ImportFn<TProps>,
  props: TProps,
) => ({
  path: replaceFirstSlash(path),
  loader: publicRouteLoader,
  ...createLazyRouteWithProps(importFn, props),
});

export const authRoute = <TProps extends object>(
  path: string,
  importFn: ImportFn<TProps>,
) => ({
  path: replaceApp(path),
  ...createLazyRouteWithAuthLoader(importFn),
});

export const authRouteWithProps = <TProps extends object>(
  path: string,
  importFn: ImportFn<TProps>,
  props: TProps,
) => ({
  path: replaceApp(path),
  ...createLazyRouteWithAuthLoaderAndProps(importFn, props),
});

type RedirectTarget =
  | string
  | ((params: LoaderFunctionArgs["params"]) => string);

/**
 * Authenticated compatibility route for URLs moved elsewhere in the app.
 * Query strings are retained and dynamic segments are supplied to the target
 * builder so bookmarked tab/detail URLs continue to resolve.
 */
export const authRedirectRoute = (path: string, to: RedirectTarget) => ({
  path: replaceApp(path),
  loader: async (args: LoaderFunctionArgs) => {
    const authResult = await authenticatedRouteLoader(args);
    if (authResult) return authResult;

    const requestUrl = new URL(args.request.url);
    const target = typeof to === "function" ? to(args.params) : to;
    return replace(`${target}${requestUrl.search}`);
  },
});
